import { Component, inject } from "@angular/core";
import { FormsDataService } from "@app/forms/services/forms-data.service";
import { FormDraft } from "@app/forms/models/detail/FormDraft";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { parseTyped } from "@methods/CommonMethods";
import { Form } from "@app/forms/models";
import { Pages } from "@constants/pages/Pages";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { firstValueFrom } from "rxjs";

@Component({
    selector: "unreleased-form-overview",
    templateUrl: "./unreleased-form-overview.component.html",
    styleUrls: ["./unreleased-form-overview.component.scss"],
    standalone: false
})
export class UnreleasedFormOverviewComponent {
    private _ds = inject(FormsDataService);
    private _pages = inject(Pages);
    private _tabService = inject(TabService);
    private _toastr = inject(ToastrService);
    private _promptService = inject(CerrixPromptService);

    loaded = false;
    callRunning = false;

    drafts: FormDraft[];

    releasedConfig: GenericListConfig;
    unreleasedConfig: GenericListConfig;

    constructor() {
        this.load();
    }

    load() {
        this.loaded = true;
        this.callRunning = true;

        this._ds.getUnreleasedFormDrafts().subscribe((drafts) => {
            this.drafts = drafts;
            this.createConfig();
            this.callRunning = false;
        });
    }

    createConfig() {
        const releasedDrafts = this.drafts.filter((x) => x.TargetFormID > 0);
        this.releasedConfig = this.getListConfig("Released form drafts", releasedDrafts, "released-form-drafts");

        const unreleasedDrafts = this.drafts.filter((x) => !x.TargetFormID || x.TargetFormID <= 0);
        this.unreleasedConfig = this.getListConfig(
            "Unreleased form drafts",
            unreleasedDrafts,
            "unreleased-form-drafts"
        );
    }

    private getListConfig(name: string, data: FormDraft[], uiid: string) {
        return <GenericListConfig>{
            name: name,
            uiid: uiid,
            uiidDelete: `${uiid}-delete`,

            allowDelete: true,
            fields: [
                {
                    fieldName: "Name",
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: "Status",
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: "DateUpdated",
                    prettyName: "Date Updated",
                    fieldType: GenericListFieldType.DateTime,
                },
                {
                    fieldName: "UpdatedBy",
                    prettyName: "Updated By",
                    fieldType: GenericListFieldType.Text,
                },
            ],

            data: data,

            editOverride: (row) => {
                const draftForm = parseTyped<Form>(row.Draft, null);
                draftForm.LoadedDraftID = row.ID;

                const tabId = row.TargetFormID > 0 ? row.TargetFormID : `draft-${row.ID}`;
                this._tabService.generateTab(this._pages.FormDetail, tabId, draftForm.Title, {
                    draftToLoad: draftForm,
                });
            },

            delete: async (row) => {
                if (
                    await this._promptService
                        .confirm(
                            "Delete draft",
                            `Are you sure you want to delete the draft: '${row.Name}'?`
                        )
                        .toPromise()
                ) {
                    await firstValueFrom(this._ds.deleteDraft(row.ID));
                    this._toastr.success("Draft has been deleted.", "Success");
                    return true;
                }

                return false;
            },
        };
    }
}
